/**
 * MapSurface — غلاف React حول سطح الخريطة المعتمد (`public/map-riyadh.html`).
 *
 * سطح الخريطة مستقل بقرار تصميمي مثبّت (DESIGN-SYSTEM §6) ويتواصل مع التطبيق
 * عبر postMessage فقط:
 *   وارد إلى التطبيق: ready · select-district · open-district · mode-changed
 *   صادر إلى الخريطة: set-mode · set-city · focus-district
 *
 * القيد المعروف (موثّق في PHASE-1-REPORT): سطح الخريطة يقرأ طبقة بيانات العرض
 * مباشرة بدلاً من المرور عبر MarketDataProvider. يُعالَج في PHASE 2 برسالة
 * `set-data` تُغذّي الخريطة من Provider.
 */

import { useEffect, useRef } from 'react';
import { color } from '@/design/tokens';
import type { CityName, MapMode } from '@/domain/types';

export interface MapSurfaceMessage {
  src?: string;
  type: 'ready' | 'select-district' | 'open-district' | 'mode-changed';
  name?: string;
  city?: string;
  mode?: MapMode;
}

export function MapSurface({
  city,
  mode,
  focusDistrictName,
  height = 440,
  onMessage,
}: {
  city: CityName;
  mode: MapMode;
  focusDistrictName?: string;
  height?: number;
  onMessage?: (m: MapSurfaceMessage) => void;
}): JSX.Element {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const readyRef = useRef(false);

  useEffect(() => {
    function handler(e: MessageEvent): void {
      const data = e.data as MapSurfaceMessage | undefined;
      if (!data || data.src !== 'nitaq-map') return;
      if (data.type === 'ready') readyRef.current = true;
      onMessage?.(data);
    }
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onMessage]);

  useEffect(() => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    win.postMessage({ type: 'set-city', city }, '*');
    win.postMessage({ type: 'set-mode', mode }, '*');
    if (focusDistrictName) {
      win.postMessage({ type: 'focus-district', name: focusDistrictName, city }, '*');
    }
  }, [city, mode, focusDistrictName]);

  return (
    <div
      style={{
        borderRadius: 16,
        overflow: 'hidden',
        border: `1px solid ${color.hairlineSoft}`,
        background: color.base800,
        minHeight: height,
      }}
    >
      <iframe
        ref={frameRef}
        title="خريطة نطاق الحرارية"
        src="/map-riyadh.html"
        style={{ width: '100%', height, display: 'block' }}
      />
    </div>
  );
}
